/**
 * NEW SHOPPING CAMPAIGN — /google-ads/shopping/create/campaign
 *
 * Same shape as the App Promotion, YouTube and Search workspaces (step rail ·
 * goal strip · sticky preview), tuned to Standard Shopping:
 *
 *   1. Campaign settings — Merchant Center feed, feed label, priority,
 *      networks, bidding, budget, dates, locations, languages.
 *   2. Ad groups — each gets an "All products" product group and a product ad
 *      (no creative: Google renders image, title and price from the feed).
 *   3. Review & save — grouped facts, every issue, and a live build log.
 *
 * The Merchant Center link itself is an account-level action in Google Ads,
 * so the wizard reads the linked accounts and says clearly when there are
 * none. Publishing is RESUMABLE and each piece is atomic on Google's side.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle2, ChevronRight, Layers, Lock, Package, Plus, Settings, ShoppingBag, Store } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { googleAdsService, GoogleAdsGeoSuggestion, GoogleAdsLanguage, ShoppingBidding, ShoppingReadiness } from '@/services/google-ads.service';
import { BackTo, errMsg, Label, Section, Switch, Tag, useYoutubeAuth } from '@/components/google-ads/youtube/youtubeShared';
import { LanguagePicker, LocationSearch } from '@/components/google-ads/youtube/TargetingPickers';
import { MoneyInput, Pub, today, toMicros } from '@/components/google-ads/objectives/objectiveShared';
import {
  BudgetDates, BuildRow, Callout, ChoiceCards, ConnectFirst, ConversionNote, Fact, FixList, GoalStrip, IntroBanner, PageSpinner, PublishPanel,
  RadioCard, RailStep, Segmented, TargetingFacts, WizardRail, WizardStepPills,
} from '@/components/google-ads/objectives/wizardChrome';
import {
  cloneShoppingGroup, newShoppingGroup, PRIORITIES, priorityLabel, SHOPPING_BIDDING, shoppingBiddingTitle, ShoppingGroupCard, ShoppingGroupDraft,
  shoppingGroupIssues, shoppingGroupSummary, ShoppingPreview,
} from '@/components/google-ads/shopping/shoppingShared';
import { SHOPPING_OBJECTIVE as CFG } from './shoppingObjective';

type StepKey = 'campaign' | 'groups' | 'review';
const STEPS: ReadonlyArray<RailStep<StepKey>> = [
  { key: 'campaign', label: 'Campaign settings', icon: Settings },
  { key: 'groups', label: 'Ad groups', icon: Layers },
  { key: 'review', label: 'Review & save', icon: CheckCircle2 },
];

export default function ShoppingCampaignWizard() {
  const navigate = useNavigate();
  const { auth, accountUid } = useYoutubeAuth();
  const ready = !!(auth?.connected && auth?.customer_selected && accountUid);
  const currency = auth?.currency ?? '';
  const [step, setStep] = useState<StepKey>('campaign');

  // ── campaign settings ──
  const [readiness, setReadiness] = useState<ShoppingReadiness | null>(null);
  const [merchantId, setMerchantId] = useState('');
  const [feedLabel, setFeedLabel] = useState('');
  const [priority, setPriority] = useState<'0' | '1' | '2'>('0');
  const [searchPartners, setSearchPartners] = useState(true);
  const [name, setName] = useState(`Shopping - ${today()}`);
  const [bidding, setBidding] = useState<ShoppingBidding>('MANUAL_CPC');
  const [ceiling, setCeiling] = useState('');
  const [targetRoas, setTargetRoas] = useState('');
  const [budget, setBudget] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [locations, setLocations] = useState<GoogleAdsGeoSuggestion[]>([]);
  const [languages, setLanguages] = useState<GoogleAdsLanguage[]>([]);
  const [takenNames, setTakenNames] = useState<Set<string>>(() => new Set());
  const manualCpc = bidding === 'MANUAL_CPC';

  useEffect(() => {
    if (!accountUid) return;
    googleAdsService.shopping.readiness(accountUid)
      .then((r) => { setReadiness(r); if (r.merchantCenters.links[0]) setMerchantId((cur) => cur || r.merchantCenters.links[0].merchantId); })
      .catch((e) => setReadiness({ conversions: { trackingStatus: null, enabledConversionActions: null, conversionBiddingAllowed: null }, merchantCenters: { links: [], warning: errMsg(e) } }));
    googleAdsService.listCampaigns(accountUid)
      .then((l) => setTakenNames(new Set(l.filter((c) => c.status !== 'REMOVED').map((c) => c.name.trim().toLowerCase()))))
      .catch(() => undefined);
  }, [accountUid]);
  const links = readiness?.merchantCenters.links ?? null;
  const conversionBlocked = readiness?.conversions.conversionBiddingAllowed === false;
  useEffect(() => { if (conversionBlocked && bidding === 'TARGET_ROAS') setBidding('MANUAL_CPC'); }, [conversionBlocked, bidding]);
  const merchantLinked = links === null ? null : links.some((l) => l.merchantId === merchantId.trim());

  // ── structure ──
  const first = useMemo(() => newShoppingGroup(1), []);
  const [groups, setGroups] = useState<ShoppingGroupDraft[]>([first]);
  const [openId, setOpenId] = useState(first.id);
  const patch = (gid: string, p: Partial<ShoppingGroupDraft>) => setGroups((gs) => gs.map((g) => (g.id === gid ? { ...g, ...p } : g)));

  // ── publish ──
  const [pub, setPub] = useState<Record<string, Pub>>({});
  const [pubError, setPubError] = useState<string | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [finished, setFinished] = useState(false);
  const madeRef = useRef<{ campaign?: string; groups: Record<string, string> }>({ groups: {} });
  const campaignLocked = !!madeRef.current.campaign;

  // ── validation ──
  const campaignIssues = useMemo(() => {
    const i: string[] = [];
    if (!/^\d{4,}$/.test(merchantId.trim())) i.push('Pick the Merchant Center account (or enter its id — digits only).');
    if (!name.trim()) i.push('Campaign name is required.');
    else if (!campaignLocked && takenNames.has(name.trim().toLowerCase())) i.push(`A campaign named "${name.trim()}" already exists on this account — Google needs a new name.`);
    if (toMicros(budget) <= 0) i.push('Enter a daily budget.');
    if (bidding === 'TARGET_ROAS' && !(Number(targetRoas) > 0)) i.push('Target ROAS needs a value above zero.');
    if (bidding === 'TARGET_ROAS' && conversionBlocked) i.push('Target ROAS needs conversion tracking, which this account does not have yet — pick Manual CPC or Maximize clicks.');
    if (start && end && end < start) i.push('The end date is before the start date.');
    return i;
  }, [merchantId, name, campaignLocked, takenNames, budget, bidding, targetRoas, conversionBlocked, start, end]);
  const groupIssues = useMemo(() => {
    const i: string[] = [];
    if (!groups.length) i.push('Add at least one ad group.');
    groups.forEach((g, n) => { const gi = shoppingGroupIssues(g, manualCpc); if (gi.length) i.push(`${g.name.trim() || `Ad group ${n + 1}`}: needs ${gi.join(', ')}.`); });
    return i;
  }, [groups, manualCpc]);
  const allIssues = [...campaignIssues, ...groupIssues];
  const issuesFor = (k: StepKey) => (k === 'campaign' ? campaignIssues.length : k === 'groups' ? groupIssues.length : 0);

  const biddingSub = bidding === 'MAXIMIZE_CLICKS' ? (toMicros(ceiling) > 0 ? `Max CPC ${ceiling} ${currency}` : 'No cap')
    : bidding === 'TARGET_ROAS' ? (Number(targetRoas) > 0 ? `Target ROAS ${Math.round(Number(targetRoas) * 100)}%` : '—') : 'Max CPC set per ad group';

  // ── publish (resumable) ──
  const publish = async () => {
    if (!accountUid || allIssues.length) return;
    setPublishing(true); setPubError(null);
    const mark = (k: string, v: Pub) => setPub((m) => ({ ...m, [k]: v }));
    const made = madeRef.current;
    try {
      if (!made.campaign) {
        mark('campaign', 'running');
        try {
          const r = await googleAdsService.shopping.createCampaign(accountUid, {
            name: name.trim(), dailyBudgetMicros: toMicros(budget), status: 'PAUSED', merchantId: merchantId.trim(),
            feedLabel: feedLabel.trim() || undefined, campaignPriority: Number(priority) as 0 | 1 | 2, searchPartners, bidding,
            cpcBidCeilingMicros: bidding === 'MAXIMIZE_CLICKS' && toMicros(ceiling) > 0 ? toMicros(ceiling) : undefined,
            targetRoas: bidding === 'TARGET_ROAS' ? Number(targetRoas) : undefined,
            startDate: start || undefined, endDate: end || undefined,
            geoTargetIds: locations.length ? locations.map((l) => l.id) : undefined,
            languageIds: languages.length ? languages.map((l) => l.id) : undefined,
          });
          made.campaign = r.campaignId;
        } catch (e) { mark('campaign', 'failed'); setPubError(`Campaign: ${errMsg(e)}`); return; }
        mark('campaign', 'done');
      }
      for (const g of groups) {
        if (made.groups[g.id]) continue;
        mark(g.id, 'running');
        try {
          const r = await googleAdsService.shopping.createAdGroup(accountUid, {
            campaignId: made.campaign!, name: g.name.trim(), status: 'PAUSED', cpcBidMicros: manualCpc ? toMicros(g.cpcBid) : undefined, withProductAd: g.withAd,
          });
          made.groups[g.id] = r.adGroupId;
        } catch (e) { mark(g.id, 'failed'); setPubError(`Ad group "${g.name.trim()}": ${errMsg(e)}`); return; }
        mark(g.id, 'done');
      }
      setFinished(true);
      toast.success('Shopping campaign published — everything is paused, so nothing spends until you enable it.');
      setTimeout(() => navigate(CFG.base), 1200);
    } finally { setPublishing(false); }
  };

  const buildRows: BuildRow[] = [
    { key: 'campaign', label: `Campaign — ${name || 'untitled'}`, level: 0, state: pub.campaign, detail: merchantId ? `Merchant Center ${merchantId}` : undefined },
    ...groups.map((g, n): BuildRow => ({
      key: g.id, label: `Ad group — ${g.name.trim() || `Ad group ${n + 1}`}`, level: 1, state: pub[g.id],
      detail: `All products${g.withAd ? ' + product ad' : ''}`,
    })),
  ];
  const Icon = CFG.icon;
  const priorityInfo = PRIORITIES.find((p) => p.value === priority)!;

  return (
    <>
      <Topbar title={`New ${CFG.title} campaign`} breadcrumb={`Google Ads / ${CFG.title}`}
        description="Standard Shopping campaign, product groups and product ads — then save everything paused"
        back={<BackTo to={CFG.base} label={`Back to ${CFG.title}`} />} />

      <div className="w-full px-7 py-7">
        {auth === undefined && <PageSpinner />}
        {auth !== undefined && !ready && <ConnectFirst what="Shopping campaigns" />}

        {ready && (
          <div className="flex w-full gap-5 pb-12">
            <WizardRail steps={STEPS} step={step} onStep={setStep} issues={issuesFor} summary={(
              <>
                <div>{merchantId ? `Merchant Center ${merchantId}` : 'No Merchant Center yet'}</div>
                <div>{groups.length} ad group{groups.length === 1 ? '' : 's'}</div>
                <div>{groups.filter((g) => g.withAd).length} product ad{groups.filter((g) => g.withAd).length === 1 ? '' : 's'}</div>
              </>
            )} />

            <div className="min-w-0 flex-1 space-y-4">
              <WizardStepPills steps={STEPS} step={step} onStep={setStep} />
              <GoalStrip items={[
                { label: 'Goal', value: CFG.goal },
                { label: 'Type', value: 'Standard Shopping' },
                { label: 'Served via', value: searchPartners ? 'Google Search & Shopping · search partners' : 'Google Search & Shopping' },
              ]}>
                <Tag>starts paused</Tag>
              </GoalStrip>

              {/* ── STEP 1: campaign ── */}
              {step === 'campaign' && (
                <>
                  {campaignLocked && (
                    <Callout icon={Lock}>The campaign already exists on Google, so its settings are locked. Fix the failing ad group and publish again.</Callout>
                  )}
                  <fieldset disabled={campaignLocked} className="min-w-0 space-y-4">
                    <IntroBanner icon={Icon} title="Product ads straight from your Merchant Center feed"
                      points={['Photo, title & price from the feed', 'No ad copy to write', 'Product groups & bids', 'Starts paused']}>
                      Shoppers see your products with their image, title and price right in Google results and the Shopping tab. You pick the feed,
                      the budget and how much to bid — Google matches each search to the right product.
                    </IntroBanner>

                    <Section title="Merchant Center" hint="The product feed these ads come from. It must already be linked to this Google Ads account.">
                      {links === null ? (
                        <div className="space-y-2">{[0, 1].map((i) => <div key={i} className="skeleton h-14 rounded-xl" />)}</div>
                      ) : links.length > 0 ? (
                        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                          {links.map((l) => (
                            <RadioCard key={l.merchantId} icon={Store} on={merchantId === l.merchantId} onClick={() => setMerchantId(l.merchantId)}
                              title={`Merchant Center ${l.merchantId}`} desc="Linked to this Google Ads account" />
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <Callout tone="warn" icon={Store}>
                            <p className="font-semibold text-text-primary">No linked Merchant Center found{readiness?.merchantCenters.warning ? ` (${readiness.merchantCenters.warning})` : ''}.</p>
                            <p className="mt-1">Link one in Google Ads under <strong>Tools → Data manager → Linked accounts → Merchant Center</strong> and reload. If it is already linked, enter its id below.</p>
                          </Callout>
                          <div className="max-w-xs">
                            <Label>Merchant Center account id</Label>
                            <Input className="mt-1.5" inputMode="numeric" placeholder="e.g. 123456789" value={merchantId} onChange={(e) => setMerchantId(e.target.value.replace(/\D+/g, ''))} />
                          </div>
                        </div>
                      )}
                      <div className="mt-4 max-w-xs">
                        <Label>Feed label <span className="font-normal text-text-muted">(optional)</span></Label>
                        <Input className="mt-1.5" maxLength={20} placeholder="e.g. AE" value={feedLabel} onChange={(e) => setFeedLabel(e.target.value.toUpperCase())} />
                        <p className="mt-1 text-2xs text-text-muted">Only products with this feed label. Blank = every feed in the account.</p>
                      </div>
                    </Section>

                    <Section title="Priority and networks">
                      <div className="space-y-4">
                        <div>
                          <Label>Campaign priority</Label>
                          <div className="mt-1.5"><Segmented options={PRIORITIES.map((p) => ({ value: p.value, label: p.label }))} value={priority} onChange={setPriority} ariaLabel="Campaign priority" /></div>
                          <p className="mt-1.5 text-2xs text-text-muted">{priorityInfo.hint}</p>
                        </div>
                        <div className="divide-y divide-border rounded-xl border border-border">
                          <div className="flex items-start gap-3 px-3.5 py-3">
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-text-primary">Search partners</div>
                              <p className="mt-0.5 text-2xs leading-4 text-text-muted">Show products on Google's search partner sites too — usually cheap extra reach.</p>
                            </div>
                            <Switch checked={searchPartners} label="Search partners" onCheckedChange={setSearchPartners} />
                          </div>
                        </div>
                        <p className="text-2xs leading-4 text-text-muted">
                          Local inventory ads are on for every Shopping campaign (Google's change of August 2026) — they only show if your Merchant Center has a local inventory feed.
                        </p>
                      </div>
                    </Section>

                    <Section title="Campaign name">
                      <Input value={name} maxLength={255} onChange={(e) => setName(e.target.value)} />
                    </Section>

                    <Section title="Bidding" hint="How much you pay for a product click.">
                      <ConversionNote readiness={readiness?.conversions ?? null} />
                      <ChoiceCards options={SHOPPING_BIDDING} value={bidding} onChange={setBidding} recommended="MANUAL_CPC" conversionBlocked={conversionBlocked} cols={3} />
                      {(bidding === 'MAXIMIZE_CLICKS' || bidding === 'TARGET_ROAS') && (
                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {bidding === 'MAXIMIZE_CLICKS' ? (
                            <div>
                              <Label>Max CPC cap <span className="font-normal text-text-muted">(optional)</span></Label>
                              <div className="mt-1.5"><MoneyInput value={ceiling} onChange={setCeiling} currency={currency} placeholder="No cap" /></div>
                            </div>
                          ) : (
                            <div>
                              <Label>Target ROAS <span className="font-normal text-text-muted">(4 = 400%)</span></Label>
                              <Input className="mt-1.5" inputMode="decimal" value={targetRoas} onChange={(e) => setTargetRoas(e.target.value)} placeholder="e.g. 4" />
                            </div>
                          )}
                        </div>
                      )}
                      {manualCpc && <p className="mt-3 text-2xs text-text-muted">You set the max CPC on each ad group's product group in the next step.</p>}
                    </Section>

                    <Section title="Budget and dates">
                      <BudgetDates budget={budget} onBudget={setBudget} start={start} onStart={setStart} end={end} onEnd={setEnd} currency={currency} />
                    </Section>

                    <Section title="Locations" hint="Where your shoppers are. Your products must be approved for these countries in Merchant Center. Empty = everywhere.">
                      <LocationSearch accountUid={accountUid} value={locations} onChange={setLocations} />
                    </Section>

                    <Section title="Languages" hint="Languages your shoppers use Google in. Empty = all languages.">
                      <LanguagePicker accountUid={accountUid} value={languages} onChange={setLanguages} />
                    </Section>
                  </fieldset>

                  <div className="flex justify-end">
                    <Button onClick={() => setStep('groups')} iconRight={<ChevronRight size={15} />}>Next: Ad groups</Button>
                  </div>
                </>
              )}

              {/* ── STEP 2: ad groups ── */}
              {step === 'groups' && (
                <>
                  <Callout icon={Package}>
                    Shopping ads carry no creative — Google builds each one from your feed. Every ad group gets an "All products" product group
                    {manualCpc ? ' at its max CPC' : ''} and, by default, one product ad: that's all a Shopping ad group needs to serve.
                  </Callout>
                  {groups.map((g, n) => (
                    <ShoppingGroupCard key={g.id} group={g} index={n} open={openId === g.id} onToggle={() => setOpenId(openId === g.id ? '' : g.id)}
                      manualCpc={manualCpc} currency={currency} state={pub[g.id]} made={!!madeRef.current.groups[g.id]} busy={publishing}
                      onChange={(p) => patch(g.id, p)}
                      onDuplicate={() => { const c = cloneShoppingGroup(g); setGroups((gs) => { const i = gs.findIndex((x) => x.id === g.id); return [...gs.slice(0, i + 1), c, ...gs.slice(i + 1)]; }); setOpenId(c.id); }}
                      onRemove={groups.length > 1 ? () => setGroups((gs) => gs.filter((x) => x.id !== g.id)) : undefined} />
                  ))}
                  <Button variant="secondary" className="w-full" iconLeft={<Plus size={15} />} disabled={publishing}
                    onClick={() => { const g = newShoppingGroup(groups.length + 1); setGroups((gs) => [...gs, g]); setOpenId(g.id); }}>Add another ad group</Button>
                  <div className="flex justify-between">
                    <Button variant="secondary" onClick={() => setStep('campaign')}>Back</Button>
                    <Button onClick={() => setStep('review')} iconRight={<ChevronRight size={15} />}>Next: Review</Button>
                  </div>
                </>
              )}

              {/* ── STEP 3: review ── */}
              {step === 'review' && (
                <>
                  <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.1fr_1fr]">
                    <Section title="Campaign" hint="Everything Google will receive for the campaign itself."
                      right={<Button variant="ghost" size="sm" className="h-7 px-2 text-2xs" onClick={() => setStep('campaign')}>Edit</Button>}>
                      <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                        <Fact label="Name" value={name || '—'} wide />
                        <Fact label="Merchant Center" value={merchantId || '—'} mono sub={merchantLinked === false ? 'not in the linked list — Google will check' : feedLabel ? `feed label ${feedLabel}` : 'every feed label'} />
                        <Fact label="Priority" value={priorityLabel(Number(priority))} sub="Local inventory ads follow your feed" />
                        <Fact label="Bidding" value={shoppingBiddingTitle(bidding)} sub={biddingSub} />
                        <Fact label="Daily budget" value={budget ? `${budget} ${currency}` : '—'}
                          sub={toMicros(budget) > 0 ? `≈ ${(Number(budget) * 30.4).toFixed(0)} ${currency} / month` : undefined} />
                        <Fact label="Schedule" value={start ? `${start} → ${end || 'no end'}` : `Starts when enabled${end ? ` → ends ${end}` : ' · no end date'}`} />
                        <Fact label="Networks" value={searchPartners ? 'Google Search & Shopping + search partners' : 'Google Search & Shopping'} />
                      </div>
                      <TargetingFacts locations={locations.map((l) => l.name)} languages={languages.map((l) => l.name)} />
                    </Section>

                    <Section title="Ad groups" hint={`${groups.length} ad group${groups.length === 1 ? '' : 's'}`}
                      right={<Button variant="ghost" size="sm" className="h-7 px-2 text-2xs" onClick={() => setStep('groups')}>Edit</Button>}>
                      <ul className="space-y-2">
                        {groups.map((g, n) => (
                          <li key={g.id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2.5">
                            <ShoppingBag size={13} className="shrink-0 text-text-muted" />
                            <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-text-primary">{g.name.trim() || `Ad group ${n + 1}`}</span>
                            <span className="shrink-0 text-2xs text-text-muted">{shoppingGroupSummary(g, manualCpc, currency)}</span>
                          </li>
                        ))}
                      </ul>
                    </Section>
                  </div>

                  <FixList groups={[{ step: 'campaign' as StepKey, issues: campaignIssues }, { step: 'groups' as StepKey, issues: groupIssues }]} onJump={setStep} />

                  <PublishPanel
                    hint="Creates everything on Google in order — the campaign with its budget and targeting, then each ad group with its product group and product ad. It all starts paused."
                    rows={buildRows} error={pubError} publishing={publishing} blocked={allIssues.length > 0} finished={finished}
                    onPublish={publish} onCancel={() => navigate(CFG.base)} />
                </>
              )}
            </div>

            {/* live preview */}
            <div className="hidden w-[320px] shrink-0 xl:block">
              <div className="sticky top-24">
                <ShoppingPreview merchantId={merchantId.trim()} merchantLinked={merchantLinked} groups={groups.length} withAds={groups.filter((g) => g.withAd).length}
                  local={false} feedLabel={feedLabel.trim() || undefined} currency={currency} />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
